/**
 * Operations on the ordered visits that make up the draft flight route.
 *
 * @remarks
 * Detects the initial trip setting, edits/reorders visit occurrences, and labels
 * repeated visits. Route edits use the shared planner to flag affected assignments;
 * selecting passenger/cargo journeys belongs to journeys.ts.
 *
 * @packageDocumentation
 */
import {AssignmentPlan, planAssignments} from '../../../../../common/flight-leg/assignment-plan';
import {createVisit, RouteVisit} from '../../../../../common/flight-leg/route-visits';
import {FlightLeg} from '../../../../../schema';

/** Labels an occurrence within visits to the same location, independently of leg numbers. */
export function visitLabel(plan: AssignmentPlan, key: string): string {
    const visit = plan.visits.find((visit) => visit.key === key);
    if (!visit) return 'Removed visit';

    const matches = plan.visits.filter((item) => item.locationID === visit.locationID);
    if (matches.length === 1) return '';

    const index = matches.findIndex((item) => item.key === key);
    if (index === 0) return 'First visit';
    if (index === matches.length - 1) return 'Final visit';

    const number = index + 1;
    const suffix =
        number % 100 >= 11 && number % 100 <= 13
            ? 'th'
            : {1: 'st', 2: 'nd', 3: 'rd'}[number % 10] || 'th';
    return ['First', 'Second', 'Third'][index]
        ? `${['First', 'Second', 'Third'][index]} visit`
        : `${number}${suffix} visit`;
}

/** Replans a changed visit sequence while preserving valid explicit assignment ranges. */
export const replanVisits = (plan: AssignmentPlan, visits: RouteVisit[]) =>
    planAssignments(visits, plan.pax, plan.cargo, plan.rows);

/** Adds or removes the final origin visit and replans affected assignments for review. */
export function changeTripType(plan: AssignmentPlan, roundTrip: boolean) {
    const visits = [...plan.visits];
    const origin = visits[0]?.locationID;
    if (!origin) throw new Error('The flight needs a departure before creating its route.');

    const closed = visits.length > 1 && visits[visits.length - 1].locationID === origin;
    if (roundTrip && !closed) visits.push(createVisit(origin));
    if (!roundTrip && closed) visits.pop();

    return replanVisits(plan, visits);
}

/** Moves a leg's destination occurrence while keeping the departure and final return fixed. */
export function moveRouteLeg(plan: AssignmentPlan, from: number, to: number, roundTrip: boolean) {
    const count = plan.visits.length - 1;
    const movable = count - (roundTrip ? 1 : 0);
    if (from < 0 || to < 0 || from >= movable || to >= movable)
        throw new Error('The final return leg cannot be moved.');
    const visits = [...plan.visits];
    const [visit] = visits.splice(from + 1, 1);
    visits.splice(to + 1, 0, visit);

    return replanVisits(plan, visits);
}

/** New routes start round trip; existing routes retain their current trip type. */
export function getInitialReturnToOrigin(
    legs: Pick<FlightLeg, 'departureID' | 'destinationID'>[]
): boolean {
    if (!legs.length) return true;
    const origin = legs[0].departureID;
    return !!origin && legs[legs.length - 1].destinationID === origin;
}
